import { supabase } from '../lib/supabase';
import { Customer, CustomerWithQuotes } from '../types/customer';

export const clientService = {
  // Obtener todos los clientes
  getCustomers: async (): Promise<Customer[]> => {
    try {
      const { data, error } = await supabase
        .from('customers')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error al obtener clientes:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error inesperado al obtener clientes:', error);
      return [];
    }
  },

  // Obtener un cliente por ID
  getCustomerById: async (id: number): Promise<Customer | null> => {
    try {
      const { data, error } = await supabase
        .from('customers')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        console.error('Error al obtener cliente por ID:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error inesperado al obtener cliente por ID:', error);
      return null;
    }
  },

  // Obtener cliente con sus cotizaciones
  getCustomerWithQuotes: async (id: number): Promise<CustomerWithQuotes | null> => {
    try {
      const { data, error } = await supabase
        .from('customers')
        .select(`
          *,
          quotes (
            id,
            product_name,
            status,
            created_at
          )
        `)
        .eq('id', id)
        .single();

      if (error) {
        console.error('Error al obtener cliente con cotizaciones:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error inesperado al obtener cliente con cotizaciones:', error);
      return null;
    }
  },

  // Crear un nuevo cliente
  createCustomer: async (customer: Omit<Customer, 'id' | 'created_at' | 'updated_at'>): Promise<Customer | null> => {
    try {
      const { data, error } = await supabase
        .from('customers')
        .insert([customer])
        .select()
        .single();

      if (error) {
        console.error('Error al crear cliente:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error inesperado al crear cliente:', error);
      return null;
    }
  },

  // Actualizar un cliente
  updateCustomer: async (id: number, customer: Partial<Omit<Customer, 'id' | 'created_at' | 'updated_at'>>): Promise<Customer | null> => {
    try {
      const { data, error } = await supabase
        .from('customers')
        .update({ ...customer, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error al actualizar cliente:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error inesperado al actualizar cliente:', error);
      return null;
    }
  },

  // Eliminar un cliente
  deleteCustomer: async (id: number): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('customers')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error al eliminar cliente:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error inesperado al eliminar cliente:', error);
      return false;
    }
  },

  // Buscar clientes por término de búsqueda
  searchCustomers: async (searchTerm: string): Promise<Customer[]> => {
    try {
      const { data, error } = await supabase
        .from('customers')
        .select('*')
        .or(`name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%,company.ilike.%${searchTerm}%,ruc.ilike.%${searchTerm}%`)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error al buscar clientes:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error inesperado al buscar clientes:', error);
      return [];
    }
  },

  // Obtener clientes por estado
  getCustomersByStatus: async (status: Customer['status']): Promise<Customer[]> => {
    try {
      const { data, error } = await supabase
        .from('customers')
        .select('*')
        .eq('status', status)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error al obtener clientes por estado:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error inesperado al obtener clientes por estado:', error);
      return [];
    }
  },

  // Obtener estadísticas de clientes
  getCustomerStats: async (): Promise<{
    total: number;
    active: number;
    inactive: number;
    prospect: number;
  }> => {
    try {
      const { data, error } = await supabase
        .from('customers')
        .select('status');

      if (error) {
        console.error('Error al obtener estadísticas de clientes:', error);
        return {
          total: 0,
          active: 0,
          inactive: 0,
          prospect: 0
        };
      }

      const stats = {
        total: data?.length || 0,
        active: data?.filter(c => c.status === 'active').length || 0,
        inactive: data?.filter(c => c.status === 'inactive').length || 0,
        prospect: data?.filter(c => c.status === 'prospect').length || 0
      };

      return stats;
    } catch (error) {
      console.error('Error inesperado al obtener estadísticas de clientes:', error);
      return {
        total: 0,
        active: 0,
        inactive: 0,
        prospect: 0
      };
    }
  },

  // Buscar cliente por email o crear uno nuevo si no existe
  findOrCreateCustomer: async (customerData: Omit<Customer, 'id' | 'created_at' | 'updated_at'>): Promise<Customer> => {
    try {
      // Primero buscar si el cliente ya existe por email
      const { data: existingCustomer, error: searchError } = await supabase
        .from('customers')
        .select('*')
        .eq('email', customerData.email)
        .single();

      if (searchError && searchError.code !== 'PGRST116') { // PGRST116 es el código para "no se encontró"
        console.error('Error al buscar cliente existente:', searchError);
      }

      // Si el cliente existe, retornarlo
      if (existingCustomer) {
        console.log('Cliente existente encontrado:', existingCustomer.email);
        return existingCustomer;
      }

      // Si no existe, crear uno nuevo con estado 'prospect' por defecto
      console.log('Creando nuevo cliente:', customerData.email);
      const newCustomerData = {
        ...customerData,
        status: 'prospect' // Los nuevos clientes se registran como prospectos
      };

      const { data: newCustomer, error: createError } = await supabase
        .from('customers')
        .insert([newCustomerData])
        .select()
        .single();

      if (createError) {
        console.error('Error al crear nuevo cliente:', createError);
        throw createError;
      }

      return newCustomer;
    } catch (error) {
      console.error('Error en findOrCreateCustomer:', error);
      throw error;
    }
  }
};

export {};